import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText
} from '../../styles'

export default function CreditsAndDebts ({
  credits,
  debts,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [loading, setLoading] = useState (true)
  const [isMounted, setMounted] = useState (false)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [showDetails, setShowDetails] = useState (false)
  const [creditsAndDebts, setCreditsAndDebts] = useState ([])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) setPeriodFilter (periodFilterOptions[0])
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()

      const filters = `?start_date=${startDate}&end_date=${endDate}&customer_id=all_customers`
        
      const { data: credits } = await cutileiApi.get (`/businesses/${businessId}/credits${filters}`, requestConfig)
      const { data: debts } = await cutileiApi.get (`/businesses/${businessId}/debts${filters}`, requestConfig)

      const creditsByMonth = Dashboard.groupEarningsByMonth (credits.sort (orderCreditsAndDebts))
      const debtsByMonth = Dashboard.groupEarningsByMonth (debts.sort (orderCreditsAndDebts))
      const months = [...new Set (Object.keys ({...creditsByMonth, ...debtsByMonth}))]
      
      setCreditsAndDebts ([
        ['Mês / ano', 'Créditos', 'Dívidas'],
        ...months.map (month => ([
          month,
          creditsByMonth[month]?.reduce ((total, credit) => total + credit.value, 0),
          debtsByMonth[month]?.reduce ((total, debt) => total + debt.value, 0)
        ]))
      ])
    } catch (error) {
      logApiError (error)
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const orderCreditsAndDebts = (a, b) => (
    DateTime.fromISO (a.date).toMillis () - DateTime.fromISO (b.date).toMillis ()
  )

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  if (!credits && !debts || credits.length === 0 && debts.length === 0) {
    return null
  }
  
  const monthCreditsAndDebts = [
    ['Origem', 'Valor (R$)'],
    ['Créditos', credits.reduce ((total, credit) => total + credit.value, 0)],
    ['Dívidas', debts.reduce ((total, debt) => total + debt.value, 0)]
  ]

  return (
    <InfoContainer>
      <SubTitle>Créditos e dívidas</SubTitle>
      <PieChart
        data={monthCreditsAndDebts}
        options={{colors: ['#44CF6C', '#FF3939']}}
      />
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Créditos e dívidas'
        onClose={toggleDetails}
      >
        <FilterContainer>
          <Select
            name='periodFilter'
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => setPeriodFilter (option)}
            containerStyles={{width: 250}}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <ColumnChart
            data={creditsAndDebts}
            options={{
              isStacked: false,
              colors: ['#44CF6C', '#FF3939'],
              chartArea: {
                top: 35, left: 110, width: '100%', height: '80%'
              },
              vAxis: {
                title: 'Valor (R$)',
                titleTextStyle: {
                  bold: true
                }
              }
            }}
          />
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
